import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Slot system based on React portals.
 */
const SlotContext = createContext({
  registerPublication: () => {},
  unregisterPublication: () => {},
  hasPublication: () => {},
  registerContainer: () => {},
  unregisterContainer: () => {},
  getContainer: () => {},
  hasContainer: () => {},
});

export const useSlots = () => useContext(SlotContext);

/**
 * SlotProvider
 *
 * Hosts in-memory registries and exposes imperative APIs via context.
 * - publications: name -> number (active publishers count)
 * - containers: name -> HTMLElement (portal targets registered by SlotConsumer)
 */
export const SlotProvider = ({ children }) => {
  const [publications, setPublications] = useState({});
  const [containers, setContainers] = useState({});

  const registerPublication = useCallback((name) => {
    setPublications((prev) => ({ ...prev, [name]: (prev[name] || 0) + 1 }));
  }, []);

  const unregisterPublication = useCallback((name) => {
    setPublications((prev) => {
      const current = prev[name] || 0;
      const next = current - 1;
      const { [name]: _removed, ...rest } = prev;
      if (next > 0) {
        return { ...rest, [name]: next };
      }
      return rest;
    });
  }, []);

  const hasPublication = useCallback((name) => !!publications[name], [publications]);

  // Container registry used as portal targets
  const registerContainer = useCallback((name, element) => {
    if (!element) return;
    setContainers((prevContainers) => ({ ...prevContainers, [name]: element }));
  }, []);

  const unregisterContainer = useCallback((name) => {
    setContainers((prevContainers) => {
      const { [name]: removed, ...rest } = prevContainers;
      return rest;
    });
  }, []);

  const getContainer = useCallback((name) => containers[name] || null, [containers]);

  const hasContainer = useCallback((name) => !!containers[name], [containers]);

  return (
    <SlotContext.Provider
      value={{
        registerPublication,
        unregisterPublication,
        hasPublication,
        registerContainer,
        unregisterContainer,
        getContainer,
        hasContainer,
      }}
    >
      {children}
    </SlotContext.Provider>
  );
};

/**
 * SlotConsumer
 *
 * Declares the visual location of a named slot by registering a DOM element
 * as the container for that slot. Props are spread onto the container element.
 */
export const SlotConsumer = ({ name, ...props }) => {
  const { registerContainer, unregisterContainer } = useSlots();
  const containerRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      registerContainer(name, node);
    }
    return () => unregisterContainer(name);
  }, [name, registerContainer, unregisterContainer]);

  return <div ref={containerRef} {...props} />;
};

/**
 * SlotPublisher
 *
 * Publishes content into a named slot. The content is rendered into the
 * corresponding consumer's container via a React portal, which preserves
 * the Publisher's surrounding context (providers/hooks work as expected).
 */
export const SlotPublisher = ({ name, children }) => {
  const { registerPublication, unregisterPublication, getContainer } = useSlots();

  useEffect(() => {
    registerPublication(name);
    return () => unregisterPublication(name);
  }, [name, registerPublication, unregisterPublication]);

  const container = getContainer(name);
  return container ? createPortal(children, container) : null;
};
